import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Printer, Users } from "lucide-react";
import { XIcon } from "../../components/icons";
import { useTranslation } from "react-i18next";

export default function PrintRequestModal({
  isOpen,
  onClose,
  totalRecipients,
  isLoading,
  onCreatePrintRequest,
  testId = "print-request-modal",
}) {
  const { t } = useTranslation();

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            id={`${testId}-backdrop`}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          />
        </Transition.Child>

        {/* Modal container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                id={testId}
                className="w-full max-w-md transform overflow-hidden rounded-xl bg-cfSecondary shadow-2xl transition-all"
              >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#eef4ff]">
                      <Printer size={20} className="text-cfPrimary" />
                    </div>
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        {t("workspace:new_print_request")}
                      </Dialog.Title>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {t("workspace:new_print_request_description")}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    id={`${testId}-close`}
                    onClick={onClose}
                    aria-label={t("workspace:close")}
                    className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none"
                  >
                    <XIcon size={20} className="inline-flex text-gray-400" aria-hidden />
                  </button>
                </div>

                {/* Body */}
                <div className="px-6 py-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="flex items-center justify-center w-16 h-16 rounded-full bg-[#eef4ff] mb-5">
                      <Users size={28} className="text-cfPrimary" />
                    </div>

                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
                      {t("workspace:total_recipients")}
                    </p>

                    <div className="text-5xl font-bold text-gray-900 tabular-nums">
                      {totalRecipients}
                    </div>

                    <p className="text-sm text-gray-500 mt-3 max-w-xs">
                      {t("workspace:available_recipient")}{" "}
                      <span className="font-semibold text-gray-700">
                        {totalRecipients}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-center gap-3 px-6 py-3 border-t border-gray-200 bg-gray-50/50 w-full">
                  <button
                    id={`${testId}-cancel`}
                    onClick={onClose}
                    className="px-4 py-2.5 w-[50%] text-sm font-medium text-gray-700 bg-white border border-cfPrimary rounded-lg hover:!bg-gray-100 transition-colors focus:outline-none"
                  >
                    {t("workspace:cancel")}
                  </button>
                  <button
                    id={`${testId}-confirm`}
                    onClick={onCreatePrintRequest}
                    disabled={isLoading}
                    className="px-4 py-2.5 w-[50%] text-sm font-medium items-center justify-center text-white border border-cfPrimary bg-cfPrimary hover:bg-cfPrimaryDark rounded-lg transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        {t("workspace:processing")}
                      </>
                    ) : (
                      <>
                        <Printer size={16} />
                        {t("workspace:print_request")}
                      </>
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
